import { first, map } from "lodash";
import { Moment } from "moment";
import { useCallback, useEffect, useState } from "react";
import { onSnapshot } from "firebase/firestore";
import Functions from "../../../callable/functions";
import SaldoBruto from "../../../models/analises/saldoBruto";
import { useUsuario } from "../../../providers/usuarioProvider";
import UsuarioDb from "../../../db/usuarioDb";
import usePromise from "../../util/usePromise";

/**
 * Saldo bruto da data, com a variação já derivada.
 *
 * Era o miolo do antigo `CardSaldoBruto`. Virou hook quando o card deixou de
 * existir: na Timeline o saldo passou a morar dentro da ficha do mês, junto da
 * competência, porque as duas coisas respondem a mesma pergunta — que mês é e
 * quanto tem. Ver `timeline/fichaDoMes.tsx`.
 *
 * Reassina o cálculo quando `atualizadoAte` do usuário muda: recalcular o mês
 * muda o saldo sem mudar a data pedida.
 */
export function useSaldoBruto(data: Moment) {
  const { visualizado } = useUsuario();
  const [atualizadoAte, setAtualizadoAte] = useState(data);

  const obterSaldoBrutoPromise = useCallback(
    // Retorno anotado e iteratee em arrow: passar `SaldoBruto.fromJson` direto
    // para o `map` do lodash (que chama com 3 argumentos) fazia o TS alargar o
    // resultado para `{}`, e todo acesso a `.saldoBruto` virava erro.
    (): Promise<SaldoBruto | undefined> =>
      new Functions()
        .obterSaldoBruto({ data: data.format() })
        .then((res) => map(res.data, (json) => SaldoBruto.fromJson(json)))
        .then((lista) => first(lista)),
    [atualizadoAte, visualizado]
  );
  const { data: saldoBruto, loading } = usePromise(obterSaldoBrutoPromise);

  useEffect(() => {
    if (visualizado) {
      return onSnapshot(
        new UsuarioDb().doc({ usuarioId: visualizado }),
        (doc) => setAtualizadoAte(doc.data()!.atualizadoAte)
      );
    }
  }, [visualizado]);

  const variacao = !saldoBruto
    ? 0
    : (saldoBruto.saldoBruto - saldoBruto.valorAplicado) /
      saldoBruto.valorAplicado;

  return { saldoBruto, loading, variacao };
}
